import { Chart, registerables, type ChartConfiguration, type ChartData } from "chart.js";
import annotationPlugin, { type EventContext } from "chartjs-plugin-annotation";

import { useLayoutEffect, useMemo, useRef } from "react";

Chart.register(...registerables, annotationPlugin);

// 値のラベル
const ACTUAL = "実績";
const PREDICTED = "予想";
const PLANNED = "納入予定";
const createOpendBoxLabel = (unit: string, suffix: string) => `開封済み${unit}残量${suffix}`;
const CHEMICAL_LIMIT_LINE = "上限";
const CHEMICAL_BORDER_LINE = "発注レベル";

// 値の表示色
const ACTUAL_BACKGROUND_COLOR = "rgba(75, 192, 192, 0.6)";
const PREDICTED_BACKGROUND_COLOR = "rgba(255, 206, 86, 0.6)";
const PLANNED_BACKGROUND_COLOR = "rgba(153, 102, 255, 0.6)";
const ACTUAL_OPENED_BOX_VOLUMES_BACKGROUND_COLOR = "rgba(36, 91, 91, 0.6)";
const PREDICTED_OPENED_BOX_VOLUMES_BACKGROUND_COLOR = "rgba(94, 76, 31, 0.6)";

const CHEMICAL_LIMIT_LINE_COLOR = "rgba(40, 94, 228, 0.6)";
const CHEMICAL_BORDER_LINE_COLOR = "rgba(228, 40, 40, 0.6)";

interface Props {
  /** X軸ラベルリスト（日付） */
  labels: string[];
  /** 実績値 */
  actuals: number[];
  /** 予測値 */
  estimates: number[];
  /** 納入予定値 */
  plans?: number[];
  /** 開封済み箱残量実績値 */
  actualOpenedBoxVolumes?: number[];
  /** 開封済み箱残量予測値 */
  estimateOpenedBoxVolumes?: number[];

  /** 在庫上限ライン  */
  limit?: number;
  /** 発注レベルライン */
  border?: number;

  /** 表タイトル */
  title: string;
  /** 上限値 */
  max?: number;
  /** 表示単位 */
  unit?: string;

  /** 箱系単位 */
  boxUnit?: string;
}

/**
 * 残量推移・使用量グラフ表示
 * @param props
 * @returns
 */
export default function TransitionChart({
  labels,
  actuals,
  estimates,
  plans,
  actualOpenedBoxVolumes,
  estimateOpenedBoxVolumes,
  limit,
  border,
  title,
  max,
  unit,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  const convertDataSet = (label: string, data: number[], backgroundColor: string) => ({
    label,
    data,
    backgroundColor,
    borderWidth: 1,
  });

  const convertDataSets = (
    actuals: number[],
    estimates: number[],
    plans?: number[],
    actualOpenedBoxVolumes?: number[],
    estimateOpenedBoxVolumes?: number[]
  ) => {
    const datasets = [
      convertDataSet(ACTUAL, actuals, ACTUAL_BACKGROUND_COLOR),
      convertDataSet(PREDICTED, estimates, PREDICTED_BACKGROUND_COLOR),
    ];
    if (plans) datasets.push(convertDataSet(PLANNED, plans, PLANNED_BACKGROUND_COLOR));
    if (actualOpenedBoxVolumes) {
      datasets.push(
        convertDataSet(
          createOpendBoxLabel(unit ?? "箱", "実績"),
          actualOpenedBoxVolumes,
          ACTUAL_OPENED_BOX_VOLUMES_BACKGROUND_COLOR
        )
      );
    }
    if (estimateOpenedBoxVolumes) {
      datasets.push(
        convertDataSet(
          createOpendBoxLabel(unit ?? "箱", "予想"),
          estimateOpenedBoxVolumes,
          PREDICTED_OPENED_BOX_VOLUMES_BACKGROUND_COLOR
        )
      );
    }
    return datasets;
  };

  const convertAnnotation = (label: string, yValue: number, backgroundColor: string) => ({
    type: "line" as "line",
    yMin: yValue,
    yMax: yValue,
    borderColor: backgroundColor,
    borderWidth: 2,
    borderDash: [],
    label: {
      display: false,
      content: `${label} ${yValue}`,
      position: "end" as "end",
    },
    enter: (ctx: EventContext) => {
      if (ctx.element.label?.options) ctx.element.label.options.display = true;
      return true;
    },
    leave(ctx: EventContext) {
      if (ctx.element.label?.options) ctx.element.label.options.display = false;
      return true;
    },
  });

  const createChartConfig = (data: ChartData): ChartConfiguration => ({
    type: "bar",
    data,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
        },
        y: {
          stacked: true,
          beginAtZero: true,
          min: 0,
          max,
          ticks: {
            callback: function (value, index, ticks) {
              return index < ticks.length - 1
                ? this.getLabelForValue(Number(value))
                : [unit ? `(${unit})` : "", this.getLabelForValue(Number(value))];
            },
          },
        },
      },
      plugins: {
        legend: {
          position: "top",
        },
        title: {
          display: true,
          text: title,
        },
        annotation: {
          annotations: {
            chemicalLimitLine:
              limit === undefined
                ? undefined
                : convertAnnotation(CHEMICAL_LIMIT_LINE, limit, CHEMICAL_LIMIT_LINE_COLOR),
            chemicalBorderLine:
              border === undefined
                ? undefined
                : convertAnnotation(CHEMICAL_BORDER_LINE, border, CHEMICAL_BORDER_LINE_COLOR),
          },
        },
      },
    },
  });

  const showChart = () => {
    // Canvasが表示されていない場合は何もしない
    if (!canvasRef.current) return;

    // Canvasの内容をいったん破棄して再生成する
    if (chartRef.current) chartRef.current.destroy();
    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const data = {
      labels: labels,
      datasets: convertDataSets(
        actuals,
        estimates,
        plans,
        actualOpenedBoxVolumes,
        estimateOpenedBoxVolumes
      ),
    };
    const config = createChartConfig(data);
    chartRef.current = new Chart(ctx, config);
  };

  useLayoutEffect(() => {
    showChart();
  }, [title, actuals, estimates, plans]);

  return (
    <div>
      <canvas ref={canvasRef} />
    </div>
  );
}
